/**
 * Local drop-capture registry.
 *
 * The app-wide `UniversalDropLayer` renders a full-screen overlay
 * (`fixed inset-0 z-[70]`) while any file is dragged over the window, and that
 * overlay *catches the drop* — routing the file by what it is (test report →
 * filing, work order → intake, …). That's the right default almost everywhere.
 *
 * But a surface that owns its OWN file handling — the email composer's
 * "Attach files", say — must not have its drops stolen by that overlay. Because
 * the composer opens as a modal (`z-50`, below the `z-70` overlay), a file
 * dragged onto it would otherwise land in the universal drop router instead of
 * the email ("if we drag into it, it better land on the email, not somewhere
 * else").
 *
 * This tiny module lets such a surface register itself as a **local drop owner**
 * for the lifetime it's mounted. While ANY owner is registered, the
 * `UniversalDropLayer` suppresses its overlay so the owner's own drop zone
 * receives the file. It's a ref-count (a module-level counter + subscribers) so
 * stacked owners compose correctly and nothing leaks when they unmount.
 *
 * The window-level `dragover` `preventDefault` in `useWindowFileDrag` keeps
 * running regardless, so a file dropped where nothing catches it still can't
 * navigate the browser away.
 */
import { useEffect, useState } from "react";

let owners = 0;
const listeners = new Set<(active: boolean) => void>();

function notify(): void {
    const active = owners > 0;
    for (const l of listeners) l(active);
}

/**
 * Register the calling component as a local drop owner while `enabled` is true.
 * Automatically deregisters on unmount (or when `enabled` flips to false).
 */
export function useCaptureWindowDrops(enabled = true): void {
    useEffect(() => {
        if (!enabled) return;
        owners += 1;
        notify();
        return () => {
            owners = Math.max(0, owners - 1);
            notify();
        };
    }, [enabled]);
}

/** True while any local drop owner is mounted — the UniversalDropLayer subscribes. */
export function useLocalDropOwnerActive(): boolean {
    const [active, setActive] = useState(owners > 0);
    useEffect(() => {
        const l = (a: boolean) => setActive(a);
        listeners.add(l);
        l(owners > 0); // sync in case an owner mounted between render and effect
        return () => {
            listeners.delete(l);
        };
    }, []);
    return active;
}
